// Updated playlist_collection_controller.js - ignores uploaded files not in CSV data

import { Controller } from "@hotwired/stimulus"
import Uploader from "../helpers/uploader"

export default class extends Controller {
  static targets = [
    "dropzone",
    "uploading",
    "formContainer",
    "unsupportedBrowserMessage",
    "unsupportedFileTypeMessage",
    "removableElementsOnUpload",
    "fileInput",
    "tracks",
    "resetCsv",
    "csvData",
    "fileInputMessage",
    "fileInputDrop",
    "tracksMissingError",
    "expectedFilesList",
    "expectedFilesContainer",
    "tracksUploaded",
    "tracksLength",
    // Add new targets for validation
    "titleField",
    "artistField",
    "validationErrors"
  ]
  static values = {
    acceptedExtensions: String,
    options: String,
    csvUploadPath: String,
  }

  static csv = null

  connect() {
    this.TRACK_FILE_EXTENSIONS = JSON.parse(this.acceptedExtensionsValue)
    this.options = JSON.parse(this.optionsValue)
    this.initUi()
    this.initUploader()
    this.uploader.init()
    this.tracksUploaded = 0
    this.tracksLength = 0
    this.expectedFiles = new Set() // Store all expected filenames from CSV
    this.ignoredFiles = new Set() // Track ignored files (not in CSV)
    let self = this

    let eventsToPrevent = ["dragenter", "dragover", "dragleave", "drop"]
    eventsToPrevent.forEach(eventName => {
      this.fileInputDropTarget.addEventListener(eventName, e => e.preventDefault(), false)
      this.fileInputDropTarget.addEventListener(eventName, e => e.stopPropagation(), false)
    })

    this.element.addEventListener('change', (event) => {
      if (event.target.dataset.column == 'FILENAME') {
        let currentIndicator = event.target.closest('tr').querySelector('[data-playlist-collection-row-indicator]')
        if (currentIndicator.innerHTML == '✓') {
          self.tracksUploaded--
        }
        currentIndicator.innerHTML = '-'
        self.syncGroupStsate()

        // Update expected files when filename is changed
        this.updateExpectedFiles()
      }

      // Add validation for TITLE and ARTIST fields
      if (event.target.dataset.column == 'TITLE' || event.target.dataset.column == 'ARTIST') {
        this.validateField(event.target)
      }
    })

    // Set up field blur validation
    this.setupFieldValidation()
  }

  // Add validation methods
  setupFieldValidation() {
    // Find all title and artist fields in the CSV data
    if (this.hasCsvDataTarget) {
      this.csvDataTarget.addEventListener('change', (event) => {
        if (event.target.classList.contains('value-field')) {
          if (event.target.dataset.column === 'TITLE' || event.target.dataset.column === 'ARTIST') {
            this.validateField(event.target)
          }
        }
      })

      this.csvDataTarget.addEventListener('blur', (event) => {
        if (event.target.classList.contains('value-field')) {
          if (event.target.dataset.column === 'TITLE' || event.target.dataset.column === 'ARTIST') {
            this.validateField(event.target)
          }
        }
      }, true)
    }
  }

  validateField(field) {
    const column = field.dataset.column

    if ((column === 'TITLE' || column === 'ARTIST') && field.value.trim() === '') {
      field.classList.add('border-red-500')
      field.classList.add('bg-red-50')

      // Show inline error message if it doesn't exist
      let errorMsg = field.nextElementSibling
      if (!errorMsg || !errorMsg.classList.contains('validation-error')) {
        errorMsg = document.createElement('div')
        errorMsg.className = 'validation-error text-red-500 text-xs mt-1'
        errorMsg.textContent = `${column} cannot be empty`
        field.parentNode.appendChild(errorMsg)
      }
    } else {
      field.classList.remove('border-red-500')
      field.classList.remove('bg-red-50')

      // Remove error message if it exists
      const errorMsg = field.nextElementSibling
      if (errorMsg && errorMsg.classList.contains('validation-error')) {
        errorMsg.remove()
      }
    }
  }

  validateCsvEditForm() {
    const errors = []

    // Make sure CSV data exists before validating
    if (!this.hasCsvDataTarget) return errors

    // Check for empty titles
    const titleRows = {}
    this.csvDataTarget.querySelectorAll('input.value-field[data-column="TITLE"]').forEach(field => {
      const rowId = field.closest('tr').getAttribute('data-row-id')
      const hasValidTitle = field.value.trim() !== ''

      if (!titleRows[rowId]) {
        titleRows[rowId] = hasValidTitle
      } else if (hasValidTitle) {
        titleRows[rowId] = true
      }
    })

    // Find rows with missing titles
    const missingTitleRows = Object.entries(titleRows)
      .filter(([rowId, hasTitle]) => !hasTitle)
      .map(([rowId]) => parseInt(rowId) + 1) // +1 for human-readable row number

    if (missingTitleRows.length > 0) {
      errors.push({
        message: `Missing track titles in rows: ${missingTitleRows.join(', ')}`,
        rows: missingTitleRows.map(num => num - 1), // Back to 0-indexed
        column: 'TITLE'
      })
    }

    // Check for empty artists
    const artistRows = {}
    this.csvDataTarget.querySelectorAll('input.value-field[data-column="ARTIST"]').forEach(field => {
      const rowId = field.closest('tr').getAttribute('data-row-id')
      const hasValidArtist = field.value.trim() !== ''

      if (!artistRows[rowId]) {
        artistRows[rowId] = hasValidArtist
      } else if (hasValidArtist) {
        artistRows[rowId] = true
      }
    })

    // Find rows with missing artists
    const missingArtistRows = Object.entries(artistRows)
      .filter(([rowId, hasArtist]) => !hasArtist)
      .map(([rowId]) => parseInt(rowId) + 1) // +1 for human-readable row number

    if (missingArtistRows.length > 0) {
      errors.push({
        message: `Missing artist names in rows: ${missingArtistRows.join(', ')}`,
        rows: missingArtistRows.map(num => num - 1), // Back to 0-indexed
        column: 'ARTIST'
      })
    }

    return errors
  }

  showValidationErrors(errors) {
    // Create or clear validation errors container
    let errorContainer

    if (this.hasValidationErrorsTarget) {
      errorContainer = this.validationErrorsTarget
      errorContainer.innerHTML = ''
    } else {
      errorContainer = document.createElement('div')
      errorContainer.classList.add('validation-errors')
      errorContainer.setAttribute('data-playlist-collection-target', 'validationErrors')
      errorContainer.className = 'bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4'

      // Insert at the top of the form or after the CSV data
      if (this.hasCsvDataTarget) {
        this.csvDataTarget.parentNode.insertBefore(errorContainer, this.csvDataTarget.nextSibling)
      } else {
        this.element.insertBefore(errorContainer, this.element.firstChild)
      }
    }

    // Add error messages
    errors.forEach(error => {
      const errorMessage = document.createElement('p')
      errorMessage.textContent = error.message
      errorContainer.appendChild(errorMessage)

      // Highlight the problematic fields
      error.rows.forEach(rowId => {
        const fields = this.csvDataTarget.querySelectorAll(`tr[data-row-id="${rowId}"] input.value-field[data-column="${error.column}"]`)
        fields.forEach(field => {
          field.classList.add('border-red-500')
          field.classList.add('bg-red-50')
        })
      })
    })

    // Scroll to the error container
    errorContainer.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  // Modified validateTracks method - removed missing files validation since we now ignore them
  validateTracks(event) {
    let hasErrors = false

    // Check for missing tracks (only for CSV files that still need uploads)
    const expectedButMissing = this.tracksLength - this.tracksUploaded
    if (this.tracksLength > 0 && expectedButMissing > 0) {
      event.preventDefault()
      this.tracksMissingErrorTarget.classList.remove('hidden')
      hasErrors = true
    } else {
      this.tracksMissingErrorTarget.classList.add('hidden')
    }

    // Check for empty titles and artists
    const validationErrors = this.validateCsvEditForm()
    if (validationErrors.length > 0) {
      event.preventDefault()
      this.showValidationErrors(validationErrors)
      hasErrors = true
    } else if (this.hasValidationErrorsTarget) {
      // Clear any existing validation errors
      this.validationErrorsTarget.innerHTML = ''
      this.validationErrorsTarget.classList.add('hidden')
    }

    return !hasErrors
  }

  // Existing methods remain unchanged...
  initUi() {
    this.ui = {
      dropzone: this.dropzoneTarget,
      upload: this.uploadTarget,
      formContainer: this.formContainerTarget,
      unsupportedBrowserMessage: this.unsupportedBrowserMessageTarget,
      unsupportedFileTypeMessage: this.unsupportedFileTypeMessageTarget,
      removableElementsOnUpload: this.removableElementsOnUploadTargets,
    }
  }

  initUploader() {
    this.uploader = new Uploader({
      dropzone: this.ui.dropzone,
      uploadPath: this.options.uploadPath,
      fileType: this.TRACK_FILE_EXTENSIONS,
      formAuthToken: this.options.formAuthToken,
      userId: this.options.userId
    })
  }

  triggerFileDialog() {
    this.fileInputTarget.click()
  }

  csvDrop(event) {
    event.preventDefault()
    const files = event.dataTransfer.files
    if (files.length > 0) {
      this.fileInputTarget.files = files
      this.fileInputTarget.dispatchEvent(new Event('input'))
    }
  }

  resetCsv(event) {
    event.preventDefault()
    this.fileInputTarget.value = ''
    this.tracksTarget.classList.add('hidden')
    this.resetCsvTarget.classList.add('hidden')
    this.csvDataTarget.classList.add('hidden')
    this.formContainerTarget.classList.remove('hidden')
    this.expectedFilesContainerTarget.classList.add('hidden')
    this.tracksLength = 0
    this.tracksUploaded = 0
    this.expectedFiles = new Set() // Reset expected files
    this.ignoredFiles = new Set() // Reset ignored files

    // Clear validation errors if they exist
    if (this.hasValidationErrorsTarget) {
      this.validationErrorsTarget.innerHTML = ''
      this.validationErrorsTarget.classList.add('hidden')
    }
  }

  csvUpload(event) {
    let formData = new FormData()
    formData.append("file", event.target.files[0])
    fetch(this.csvUploadPathValue, {
      method: "POST",
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': this.options.formAuthToken
      },
      credentials: 'same-origin',
    })
      .then(response => {
        if (response.ok) {
          this.fileInputMessageTarget.classList.remove('hidden')
          return response.text()
        } else {
          return response.json()
        }
      })
      .then(response => {
        if (typeof response === 'object') {
          this.fileInputMessageTarget.innerHTML = response.error
          this.fileInputMessageTarget.classList.remove('hidden')
          return
        }
        this.csvDataTarget.innerHTML = response
        this.formContainerTarget.classList.add('hidden')
        this.resetCsvTarget.classList.remove('hidden')
        this.csvDataTarget.classList.remove('hidden')
        this.tracksTarget.classList.remove('hidden')
        this.tracksLength = this.csvDataTarget.querySelectorAll('[data-playlist-collection-row-indicator]').length
        this.tracksLengthTarget.innerHTML = this.tracksLength

        // Reset tracking data
        this.expectedFiles = new Set()
        this.ignoredFiles = new Set()

        // Extract and display expected filenames from CSV
        this.updateExpectedFiles()

        // Set up field validation after CSV is loaded
        this.setupFieldValidation()
      })
      .catch(error => console.error('Error:', error))
  }

  // New method to extract and update expected files list from CSV
  updateExpectedFiles() {
    // Clear existing set
    this.expectedFiles = new Set()

    // Get all filenames from CSV
    const filenameFields = this.csvDataTarget.querySelectorAll('[data-column="FILENAME"] input.value-field')
    filenameFields.forEach(field => {
      if (field.value && field.value.trim() !== '') {
        this.expectedFiles.add(field.value.trim())
      }
    })

    // Update the expected files list UI
    this.displayExpectedFiles()
  }

  // Display expected files in UI
  displayExpectedFiles() {
    if (this.expectedFiles.size > 0) {
      // Create list of expected files with status indicators
      const filesList = Array.from(this.expectedFiles).map(filename => {
        const indicator = this.csvDataTarget.querySelector(`[data-playlist-collection-row-indicator="${filename}"]`)
        const status = indicator ? indicator.innerHTML : '-'
        const statusClass = status === '✓' ? 'text-green-500' : (status === '...' ? 'text-yellow-500' : 'text-gray-500')

        return `<li class="flex items-center my-1">
          <span class="${statusClass} mr-2">${status}</span>
          <span>${filename}</span>
        </li>`
      }).join('')

      this.expectedFilesListTarget.innerHTML = `<ul class="list-none pl-0">${filesList}</ul>`
      this.expectedFilesContainerTarget.classList.remove('hidden')
    } else {
      this.expectedFilesContainerTarget.classList.add('hidden')
    }
  }

  start(event) {
    if (this.csvDataTarget.querySelector('[data-playlist-collection-row-indicator="'+event.detail.resumableFile.fileName+'"]')) {
      this.csvDataTarget.querySelector('[data-playlist-collection-row-indicator="'+event.detail.resumableFile.fileName+'"]').innerHTML = '...'
      this.displayExpectedFiles() // Update status in expected files list
    }
  }

  complete(event) {
    const fileName = event.detail.fileName
    const rowIndicator = this.csvDataTarget.querySelector('[data-playlist-collection-row-indicator="'+fileName+'"]')

    if (rowIndicator) {
      // File matches a CSV entry - process it normally
      rowIndicator.innerHTML = '✓'
      this.tracksUploaded++
      this.syncGroupStsate()
      this.displayExpectedFiles() // Update status in expected files list

      const fields = [
        { name: "tracks[][file_identifier]", value: event.detail.uniqueIdentifier },
        { name: "tracks[][file_size]", value: event.detail.file.size },
        { name: "tracks[][file_type]", value: event.detail.file.type },
        { name: "tracks[][original_filename]", value: fileName },
        { name: "tracks[][chunks_count]", value: event.detail.chunks.length },
      ]
      fields.forEach(field => {
        const input = document.createElement("input")
        input.type = "hidden"
        input.name = field.name
        input.value = field.value
        this.tracksTarget.appendChild(input)
      })

      if (this.tracksUploaded === this.tracksLength) {
        this.tracksMissingErrorTarget.classList.add('hidden')
      }
    } else {
      // File doesn't match any CSV entry - ignore it silently
      this.ignoredFiles.add(fileName)
      console.log(`Ignoring uploaded file "${fileName}" - not found in CSV data`)
    }
  }

  error(event) {
    console.error('error on upload', event.detail)
  }

  stateUnsupportedBrowser() {
    this.ui.unsupportedBrowserMessage.style.display = 'block'
    this.ui.dropzone.style.opacity = 0
  }

  stateUnsupportedFileType() {
    this.ui.unsupportedFileTypeMessage.style.display = 'block'
  }

  toggleTableHeader(event) {
    let targetBand = event.currentTarget.dataset.artist
    event.currentTarget.querySelector('svg').classList.toggle('rotate-180')
    this.element.querySelectorAll('[data-artist-group="' + targetBand + '"]').forEach((element) => {
      element.classList.toggle("collapsed")
    })
  }

  syncGroupStsate() {
    let thisElement = this.element
    thisElement.querySelectorAll('[data-artist]').forEach((group) => {
      let targetBand = group.dataset.artist
      let groupCounter = 0
      thisElement.querySelectorAll('[data-artist-group="' + targetBand + '"] tbody tr').forEach((element) => {
        let currentIndicator = element.querySelector('[data-playlist-collection-row-indicator]')
        if (currentIndicator.innerHTML == '✓') {
          groupCounter++
        }
      })
      group.querySelector('[data-playlist-collection-group-indicator]').innerHTML = groupCounter
    })
    this.tracksLengthTarget.innerHTML = this.tracksLength
    this.tracksUploadedTarget.innerHTML = this.tracksUploaded
    this.displayExpectedFiles() // Update expected files display with latest status
  }
}
